import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { observer } from 'mobx-react'
import { Block, Heading, Paragraph } from 'suomifi-ui-components'
import { UaetUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import {
  validateContactEmail,
  validateContactPhoneNumber,
} from 'todistukset-common/src/vtj/validation'
import {
  validateUaetMailDeliveryCountry,
  validateUaetMailDeliveryFirstName,
  validateUaetMailDeliveryLastName,
  validateUaetMailDeliveryPostalCode,
  validateUaetMailDeliveryPostOffice,
  validateUaetMailDeliveryStreetAddress,
  validateUaetMailDeliveryStreetAddressExtras,
} from 'todistukset-common/src/vtj/uaet-validation'
import { normalizeTextInputWhitespace } from 'todistukset-common/src/vtj/form-sanitization.util'
import { mailDeliveryFields } from 'todistukset-common/src/vtj/mail-delivery.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  FormContainer,
  StyledTextInput,
  TextInputs,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { useUaetForm } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import {
  FirstErrorImperativeHandle,
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { TextInputColumns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import { useUaetFormActions } from 'todistukset-ui/src/vtj/stores/actions/uaet-form-actions'

const UaetContactInfoStep: StepComponent = observer(
  forwardRef(function UaetContactInfoStep({ titleId }, ref) {
    const mailDeliveryErrorRef = useRef<FirstErrorImperativeHandle>(null)
    const contactInfoErrorRef = useRef<FirstErrorImperativeHandle>(null)
    useImperativeHandle(
      ref,
      () => ({
        getFirstErrorField: () =>
          mailDeliveryErrorRef.current?.getFirstErrorField() ??
          contactInfoErrorRef.current?.getFirstErrorField() ??
          null,
      }),
      []
    )

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="aetContactInfoMainTitle" />
          </Heading>
          <Paragraph>
            <LocalizedString stringKey="uaetContactInfoMainDescription" />
          </Paragraph>
        </Block>
        <Block variant="section">
          <MailDelivery ref={mailDeliveryErrorRef} />
        </Block>
        <Block>
          <ContactInfo ref={contactInfoErrorRef} />
        </Block>
      </FormContainer>
    )
  })
)

const MailDelivery: React.FC<React.RefAttributes<FirstErrorImperativeHandle>> =
  observer(
    forwardRef(function MailDelivery(_, ref) {
      const { t } = useTranslation()
      const form = useUaetForm()

      const {
        steps: {
          [OrderFlowName.UAETTODISTUS]: {
            showStepValidationErrors: showErrors,
          },
        },
      } = useStore()

      const refs = useFirstErrorImperativeHandle(
        ref,
        form,
        getMailDeliveryErrors,
        mailDeliveryFields
      )

      const errorStatusParams = {
        form,
        showErrors,
        t,
      }

      const {
        setMailDeliveryFirstName,
        setMailDeliveryLastName,
        setMailDeliveryStreetAddress,
        setMailDeliveryStreetAddressExtras,
        setMailDeliveryPostalCode,
        setMailDeliveryPostOffice,
        setMailDeliveryCountry,
      } = useUaetFormActions()

      return (
        <>
          <Heading variant="h3">
            <LocalizedString stringKey="orderWizardDeliveryMethodStepDeliveryAddressTitle" />
          </Heading>
          <TextInputColumns>
            <StyledTextInput
              id="mail-delivery-first-name"
              data-test-id="mail-delivery-first-name"
              type="text"
              fullWidth
              statusTextAriaLiveMode="polite"
              name="first-name"
              labelText={t(
                'orderWizardUaetContactInfoStepInputLabel_firstName'
              )}
              value={finalEditableFieldValue(form.mailDelivery.firstName) || ''}
              forwardedRef={refs.firstName}
              {...errorStatus({
                ...errorStatusParams,
                field: 'firstName',
              })}
              onChange={setMailDeliveryFirstName}
            />
            <StyledTextInput
              id="mail-delivery-last-name"
              data-test-id="mail-delivery-last-name"
              type="text"
              fullWidth
              statusTextAriaLiveMode="polite"
              name="last-name"
              labelText={t('orderWizardUaetContactInfoStepInputLabel_lastName')}
              value={finalEditableFieldValue(form.mailDelivery.lastName) || ''}
              forwardedRef={refs.lastName}
              {...errorStatus({
                ...errorStatusParams,
                field: 'lastName',
              })}
              onChange={setMailDeliveryLastName}
            />
            <StyledTextInput
              id="mail-delivery-street-address"
              data-test-id="mail-delivery-street-address"
              type="text"
              fullWidth
              statusTextAriaLiveMode="polite"
              name="steet-address"
              labelText={t(
                'orderWizardDeliveryMethodStepInputLabel_streetAddress'
              )}
              value={
                finalEditableFieldValue(form.mailDelivery.streetAddress) || ''
              }
              forwardedRef={refs.streetAddress}
              {...errorStatus({
                ...errorStatusParams,
                field: 'streetAddress',
              })}
              onChange={setMailDeliveryStreetAddress}
            />
            <StyledTextInput
              id="mail-delivery-street-address-extras"
              data-test-id="mail-delivery-street-address-extras"
              type="text"
              fullWidth
              statusTextAriaLiveMode="polite"
              name="steet-address-extras"
              labelText={t(
                'orderWizardDeliveryMethodStepInputLabel_streetAddressExtras'
              )}
              value={
                finalEditableFieldValue(
                  form.mailDelivery.streetAddressExtras
                ) || ''
              }
              forwardedRef={refs.streetAddressExtras}
              {...errorStatus({
                ...errorStatusParams,
                field: 'streetAddressExtras',
              })}
              onChange={setMailDeliveryStreetAddressExtras}
            />
            <StyledTextInput
              id="mail-delivery-postal-code"
              data-test-id="mail-delivery-postal-code"
              type="text"
              fullWidth
              statusTextAriaLiveMode="polite"
              name="postal-code"
              labelText={t(
                'orderWizardDeliveryMethodStepInputLabel_postalCode'
              )}
              value={
                finalEditableFieldValue(form.mailDelivery.postalCode) || ''
              }
              forwardedRef={refs.postalCode}
              {...errorStatus({
                ...errorStatusParams,
                field: 'postalCode',
              })}
              onChange={setMailDeliveryPostalCode}
            />
            <StyledTextInput
              id="mail-delivery-post-office"
              data-test-id="mail-delivery-post-office"
              type="text"
              fullWidth
              statusTextAriaLiveMode="polite"
              name="postal-office"
              labelText={t(
                'orderWizardDeliveryMethodStepInputLabel_postOffice'
              )}
              value={
                finalEditableFieldValue(form.mailDelivery.postOffice) || ''
              }
              forwardedRef={refs.postOffice}
              {...errorStatus({
                ...errorStatusParams,
                field: 'postOffice',
              })}
              onChange={setMailDeliveryPostOffice}
            />
            <StyledTextInput
              id="mail-delivery-country"
              data-test-id="mail-delivery-country"
              type="text"
              fullWidth
              statusTextAriaLiveMode="polite"
              name="country"
              labelText={t('orderWizardDeliveryMethodStepInputLabel_country')}
              value={finalEditableFieldValue(form.mailDelivery.country) || ''}
              forwardedRef={refs.country}
              {...errorStatus({
                ...errorStatusParams,
                field: 'country',
              })}
              onChange={setMailDeliveryCountry}
            />
          </TextInputColumns>
        </>
      )
    })
  )

const ContactInfo: React.FC<React.RefAttributes<FirstErrorImperativeHandle>> =
  observer(
    forwardRef(function ContactInfo(_, ref) {
      const { t } = useTranslation()
      const form = useUaetForm()
      const {
        steps: {
          [OrderFlowName.UAETTODISTUS]: {
            showStepValidationErrors: showErrors,
          },
        },
      } = useStore()

      const refs = useFirstErrorImperativeHandle(
        ref,
        form,
        getContactInfoErrors,
        ['phoneNumber', 'email']
      )

      const errorStatusParams = {
        form,
        showErrors,
        t,
      }

      const { setContactPhone, setContactEmail } = useUaetFormActions()

      return (
        <>
          <Heading variant="h3">
            <LocalizedString stringKey="orderWizardDeliveryMethodStepContactInfoTitle" />
          </Heading>
          <Block variant="section">
            <TextInputs>
              <StyledTextInput
                id="contactInfoPhoneNumber"
                name="contactInfoPhoneNumber"
                labelText={t('aetContactInfoPhoneNumberFieldLabel')}
                type="tel"
                value={
                  finalEditableFieldValue(
                    form.spouseA.contactInfo.phoneNumber
                  ) || ''
                }
                onChange={setContactPhone}
                onBlur={() =>
                  setContactPhone(
                    normalizeTextInputWhitespace(
                      finalEditableFieldValue(
                        form.spouseA.contactInfo.phoneNumber
                      ) ?? ''
                    )
                  )
                }
                fullWidth
                forwardedRef={refs.phoneNumber}
                {...errorStatus({
                  ...errorStatusParams,
                  field: 'phoneNumber',
                })}
              />
              <StyledTextInput
                id="contactInfoEmail"
                name="contactInfoEmail"
                labelText={t('aetContactInfoEmailFieldLabel')}
                type="email"
                value={
                  finalEditableFieldValue(form.spouseA.contactInfo.email) || ''
                }
                onChange={setContactEmail}
                /* This doesn't actually clear spaces from the input element, unsure if that's on
                   SFIDF or browser implementation of type="email" field. But at least the form
                   field gets trimmed regardless of what the browser does: */
                onBlur={() =>
                  setContactEmail(
                    (
                      finalEditableFieldValue(form.spouseA.contactInfo.email) ??
                      ''
                    ).trim()
                  )
                }
                fullWidth
                forwardedRef={refs.email}
                {...errorStatus({
                  ...errorStatusParams,
                  field: 'email',
                })}
              />
            </TextInputs>
          </Block>
        </>
      )
    })
  )

const errorStatus = (params: {
  field:
    | keyof ReturnType<typeof getMailDeliveryErrors>
    | keyof ReturnType<typeof getContactInfoErrors>
  form: UaetUiForm
  showErrors: boolean
  t: TFunction
}) => {
  return getErrorStatus({
    ...params,
    errors: {
      ...getMailDeliveryErrors(params.form),
      ...getContactInfoErrors(params.form),
    },
  })
}

function getMailDeliveryErrors(form: UaetUiForm) {
  return {
    firstName: validateUaetMailDeliveryFirstName(form.mailDelivery.firstName),
    lastName: validateUaetMailDeliveryLastName(form.mailDelivery.lastName),
    streetAddress: validateUaetMailDeliveryStreetAddress(
      form.mailDelivery.streetAddress
    ),
    streetAddressExtras: validateUaetMailDeliveryStreetAddressExtras(
      form.mailDelivery.streetAddressExtras
    ),
    postalCode: validateUaetMailDeliveryPostalCode(
      form.mailDelivery.postalCode
    ),
    postOffice: validateUaetMailDeliveryPostOffice(
      form.mailDelivery.postOffice
    ),
    country: validateUaetMailDeliveryCountry(form.mailDelivery.country),
  }
}

function getContactInfoErrors(form: UaetUiForm) {
  return {
    phoneNumber: validateContactPhoneNumber(
      form.spouseA.contactInfo?.phoneNumber?.ilmoitettuArvo
    ),
    email: validateContactEmail(
      form.spouseA.contactInfo?.email?.ilmoitettuArvo
    ),
  }
}

export default UaetContactInfoStep
